"use client";

import { FC } from "react";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { LostIllustration } from "./lost-illustration";
import { PanelPattern } from "./panel-pattern";
import { Icon, type IconSvgElement } from "@/components/ui/icon";
import { ArrowRight02Icon, ArrowUpRight01Icon, CalculatorIcon, Call02Icon, Home01Icon, Location01Icon, Restaurant02Icon, StarIcon, TelegramIcon } from "@hugeicons/core-free-icons";

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const PHONE = "+375 (33) 623-78-85";
const PHONE_HREF = "tel:+375336237885";
const TELEGRAM = "https://t.me/okfood_support";

/** Куда человек скорее всего шёл, раз попал на 404. */
const SHORTCUTS: {
  href: string;
  icon: IconSvgElement;
  title: string;
  note: string;
}[] = [
  {
    href: "/#products",
    icon: Restaurant02Icon,
    title: "Рационы",
    note: "Меню на неделю вперёд",
  },
  {
    href: "/#calculator",
    icon: CalculatorIcon,
    title: "Калькулятор",
    note: "Норма калорий и БЖУ",
  },
  {
    href: "/#delivery-zone",
    icon: Location01Icon,
    title: "Доставка",
    note: "Зона и время по Минску",
  },
  {
    href: "/#reviews",
    icon: StarIcon,
    title: "Отзывы",
    note: "Что говорят клиенты",
  },
];

export const NotFoundView: FC = () => {
  const reduceMotion = useReducedMotion();
  const rise = (delay: number) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 16 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.6, delay, ease: EASE },
        };

  return (
    <main className="flex min-h-dvh flex-col bg-whitePrimary">
      <Container className="flex w-full flex-1 flex-col justify-center py-6 md:py-12">
        {/* ===== Тёмная панель — тот же язык, что и hero на главной ===== */}
        <motion.section
          {...(reduceMotion
            ? {}
            : {
                initial: { opacity: 0, y: 24 },
                animate: { opacity: 1, y: 0 },
                transition: { duration: 0.7, ease: EASE },
              })}
          className="relative mx-auto w-full max-w-[1000px] overflow-hidden rounded-[26px] bg-colorPrimary px-5 pb-9 pt-6 text-center md:rounded-[34px] md:px-12 md:pb-12 md:pt-8"
        >
          <PanelPattern />

          <div className="relative z-[1] flex flex-col items-center">
            {/* w-full обязателен: в flex-колонке с items-center обёртка не
                тянется, и <svg> без размеров схлопывается в дефолтные 300px. */}
            <motion.div {...rise(0.1)} className="w-full">
              <LostIllustration className="mx-auto w-full max-w-[320px] min-[425px]:max-w-[420px] md:max-w-[600px]" />
            </motion.div>

            <motion.span
              {...rise(0.2)}
              className="mt-2 inline-flex items-center gap-2 rounded-full border border-yellowPrimary/25 bg-yellowPrimary/10 px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.18em] text-yellowPrimary md:text-[12px]"
            >
              Ошибка 404
            </motion.span>

            <motion.h1
              {...rise(0.28)}
              className="mt-4 text-[26px] font-black leading-[1.12] tracking-tight text-whitePrimary min-[425px]:text-[32px] md:text-[46px]"
            >
              Этой страницы нет{" "}
              <span className="relative inline-block whitespace-nowrap">
                в меню
                <svg
                  aria-hidden
                  viewBox="0 0 180 12"
                  fill="none"
                  preserveAspectRatio="none"
                  className="absolute -bottom-1 left-0 h-[7px] w-full md:-bottom-2 md:h-[9px]"
                >
                  <motion.path
                    d="M3 9C36 3.5 104 2 177 6.5"
                    stroke="#d0ed1c"
                    strokeWidth="5"
                    strokeLinecap="round"
                    initial={
                      reduceMotion ? undefined : { pathLength: 0, opacity: 0 }
                    }
                    animate={
                      reduceMotion ? undefined : { pathLength: 1, opacity: 1 }
                    }
                    transition={{
                      pathLength: { duration: 0.7, delay: 0.62, ease: EASE },
                      opacity: { duration: 0.01, delay: 0.62 },
                    }}
                  />
                </svg>
              </span>
            </motion.h1>

            <motion.p
              {...rise(0.36)}
              className="mx-auto mt-5 max-w-[520px] text-[15px] leading-7 text-whitePrimary/70 md:text-[17px]"
            >
              Ссылка устарела или в адресе опечатка. С обедом всё в порядке —
              вернитесь на главную или сразу выберите рацион.
            </motion.p>

            <motion.div
              {...rise(0.44)}
              className="mt-7 flex w-full flex-col items-center justify-center gap-3 sm:flex-row"
            >
              <Button
                asChild
                size="lg"
                className="group w-full bg-yellowPrimary py-6 shadow-lg shadow-yellowPrimary/20 transition-all hover:bg-yellowPrimary/90 hover:shadow-yellowPrimary/40 sm:w-[230px]"
              >
                <Link href="/">
                  <Icon icon={Home01Icon} size={17} className="text-colorPrimary" />
                  <span className="font-bold text-colorPrimary">
                    На главную
                  </span>
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="ghost"
                className="group w-full rounded-md border border-white/20 bg-white/10 py-6 text-whitePrimary backdrop-blur-sm transition-colors hover:bg-white/20 hover:text-whitePrimary sm:w-[230px]"
              >
                <Link href="/#products">
                  <span className="font-semibold">Выбрать рацион</span>
                  <Icon icon={ArrowRight02Icon}
                    strokeWidth={0.6}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </Link>
              </Button>
            </motion.div>
          </div>
        </motion.section>

        {/* ===== Быстрые переходы: 404 не должен быть тупиком ===== */}
        <motion.div
          {...rise(0.55)}
          className="mx-auto mt-8 w-full max-w-[1000px]"
        >
          <div className="mb-3 flex items-center gap-3">
            <h2 className="text-[13px] font-extrabold uppercase tracking-[0.16em] text-colorPrimary/40">
              Куда дальше
            </h2>
            <span className="h-px flex-1 bg-colorPrimary/[0.08]" />
          </div>

          <nav
            aria-label="Основные разделы сайта"
            className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4"
          >
            {SHORTCUTS.map(({ href, icon, title, note }) => (
              <Link
                key={href}
                href={href}
                /* На узких экранах — строка, на широких — вертикальная
                   карточка: в четыре колонки горизонтальной раскладке не
                   хватает ширины и подпись обрезается. */
                className="group relative flex items-center gap-3.5 overflow-hidden rounded-2xl border border-colorPrimary/[0.07] bg-white p-4 transition-colors hover:border-yellow-hover/45 lg:flex-col lg:items-start lg:gap-0 lg:p-5"
              >
                {/* Лаймовая заливка, наезжающая из угла при наведении */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute -right-8 -top-8 h-20 w-20 rounded-full bg-yellowPrimary/0 blur-xl transition-colors duration-300 group-hover:bg-yellowPrimary/30"
                />

                <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-yellowPrimary/15 text-yellow-hover transition-colors duration-200 group-hover:bg-yellowPrimary group-hover:text-colorPrimary lg:h-12 lg:w-12">
                  <Icon icon={icon} size={19} />
                </span>

                <span className="relative min-w-0 flex-1 text-left lg:mt-4 lg:flex-none">
                  <span className="block text-[15px] font-bold text-colorPrimary">
                    {title}
                  </span>
                  <span className="mt-0.5 block text-[12.5px] leading-snug text-greySecondary">
                    {note}
                  </span>
                </span>

                <Icon icon={ArrowUpRight01Icon}
                  size={16}
                  className="relative shrink-0 text-colorPrimary/25 transition-all duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-yellow-hover lg:absolute lg:right-4 lg:top-4"
                />
              </Link>
            ))}
          </nav>
        </motion.div>

        {/* ===== Связь с людьми — на случай, если разделы не помогли ===== */}
        <motion.div
          {...rise(0.62)}
          className="mx-auto mt-7 flex w-full max-w-[1000px] flex-col items-center gap-3 sm:flex-row sm:justify-center"
        >
          <span className="text-[13.5px] text-greySecondary">
            Не нашли, что искали?
          </span>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Link
              href={TELEGRAM}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-10 items-center gap-2 rounded-xl border border-colorPrimary/10 bg-white px-4 text-[13.5px] font-bold text-colorPrimary transition-colors hover:border-yellow-hover/45 hover:bg-yellowPrimary/10"
            >
              <Icon icon={TelegramIcon} size={16} className="text-yellow-hover" />
              Написать в Telegram
            </Link>
            <Link
              href={PHONE_HREF}
              className="flex h-10 items-center gap-2 rounded-xl border border-colorPrimary/10 bg-white px-4 text-[13.5px] font-bold text-colorPrimary transition-colors hover:border-yellow-hover/45 hover:bg-yellowPrimary/10"
            >
              <Icon icon={Call02Icon} size={15} className="text-yellow-hover" />
              {PHONE}
            </Link>
          </div>
        </motion.div>
      </Container>
    </main>
  );
};
